// Every function here is a 1:1 port of a fetch call in
// enrollment-portal/staff.html — same URL, same method, same payload shape.
// Points at ops-enrollment-api — a dedicated Cloud Run copy of
// enrollment-api, deployed separately so changes here never risk the
// production apply.littlescholarsnyc.com/staff tool directors use daily.
// Same underlying GHL/BQ data, different deployable. See
// ops-enrollment-api/ for the backend source. If a field looks unused or
// oddly named, that's inherited from the existing GHL/BQ data model —
// check project doc 08-bigquery-structure before "fixing" it here.

import type { EnrollmentRow, StaffNote, StartDateHistoryEntry } from './types'

const API_BASE = import.meta.env.VITE_ENROLLMENT_API_BASE ?? 'https://ops-enrollment-api-REPLACE-uc.a.run.app'

async function authFetch(token: string, path: string, options: RequestInit = {}): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...(options.headers ?? {}), Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) {
    throw new AuthExpiredError()
  }
  return res
}

export class AuthExpiredError extends Error {
  constructor() {
    super('Session expired. Please sign in again.')
    this.name = 'AuthExpiredError'
  }
}

async function okJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new Error(body?.detail ?? `Request failed (${res.status})`)
  }
  return res.json()
}

// ── Auth ─────────────────────────────────────────────────────────────────

export async function fetchStaffConfig(): Promise<{ client_id: string }> {
  return okJson(await fetch(`${API_BASE}/staff/config`))
}

export interface StaffAuthResult {
  email: string
  name: string
  role: string
  location?: string
  id_token: string
}

export async function exchangeAuthCode(code: string, redirectUri: string): Promise<StaffAuthResult> {
  const res = await fetch(`${API_BASE}/staff/auth-code`, {
    credentials: 'include', // lets the browser store the refresh cookie
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirect_uri: redirectUri }),
  })
  return okJson(res)
}

export async function validateStaffToken(token: string): Promise<StaffAuthResult> {
  const res = await fetch(`${API_BASE}/staff/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
  return okJson(res)
}

// ── Enrollment list ──────────────────────────────────────────────────────

export async function fetchEnrollments(
  token: string,
  section: 'regular' | 'camp',
  opts: { forceRefresh?: boolean } = {},
): Promise<EnrollmentRow[]> {
  const endpoint = section === 'camp' ? '/staff/camp-enrollments' : '/staff/enrollments'
  const qs = opts.forceRefresh ? '?refresh=true' : ''
  const data = await okJson<{ enrollments: EnrollmentRow[] }>(await authFetch(token, `${endpoint}${qs}`))
  return dedupeByDocumentId(data.enrollments ?? [])
}

// staff.html dedupes by document_id, preferring the record that has a child
// name (some rows arrive with a blank name from an in-progress GHL sync).
function dedupeByDocumentId(rows: EnrollmentRow[]): EnrollmentRow[] {
  const seen = new Map<string, EnrollmentRow>()
  for (const r of rows) {
    if (!r.document_id) continue
    const existing = seen.get(r.document_id)
    if (!existing) {
      seen.set(r.document_id, r)
    } else if (!existing.child_first_name && r.child_first_name) {
      seen.set(r.document_id, r)
    }
  }
  return Array.from(seen.values())
}

// ── Enrollment actions ───────────────────────────────────────────────────

export async function updateEnrollmentStatus(
  token: string,
  payload: { contact_id: string; document_id: string; status: 'waiting' | 'started' | 'cancelled' },
): Promise<void> {
  await okJson(
    await authFetch(token, '/staff/enrollment/status', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
}

export async function cancelEnrollment(
  token: string,
  payload: { contact_id: string; document_id: string; opportunity_id?: string; reason: string },
): Promise<void> {
  await okJson(
    await authFetch(token, '/staff/enrollment/cancel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
}

export interface UpdateStartDatePayload {
  contact_id: string
  opportunity_id?: string
  document_id?: string
  client_signing_url?: string
  new_start_date: string
  reason: string
  is_camp: boolean
}

export async function updateStartDate(
  token: string,
  payload: UpdateStartDatePayload,
): Promise<{ new_age_string: string }> {
  return okJson(
    await authFetch(token, '/staff/enrollment/start-date', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
}

export async function fetchStartDateHistory(token: string, documentId: string): Promise<StartDateHistoryEntry[]> {
  const data = await okJson<{ history: StartDateHistoryEntry[] }>(
    await authFetch(token, `/staff/enrollment/start-date-history/${documentId}`),
  )
  return data.history ?? []
}

export interface EnrollmentFieldsPayload {
  document_id: string
  contact_id: string
  client_signing_url?: string
  child_first_name?: string | null
  child_last_name?: string | null
  child_dob?: string | null
  sex?: string | null
  program?: string | null
  schedule?: string | null
  num_weeks?: number | null
  location_name?: string | null
  enrollment_type?: string | null
  invoice_paid_at?: string | null
  doc_status?: string | null
}

export async function updateEnrollmentFields(token: string, payload: EnrollmentFieldsPayload): Promise<void> {
  await okJson(
    await authFetch(token, '/staff/enrollment/fields', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
}

export async function hideEnrollment(token: string, documentId: string, reason: string): Promise<void> {
  await okJson(
    await authFetch(token, '/staff/enrollment/hide', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_id: documentId, reason }),
    }),
  )
}

// ── Notes ────────────────────────────────────────────────────────────────

export async function fetchNotes(token: string, documentId: string): Promise<StaffNote[]> {
  const data = await okJson<{ notes: StaffNote[] }>(await authFetch(token, `/staff/notes/${documentId}`))
  return data.notes ?? []
}

export async function saveNote(
  token: string,
  payload: { contact_id: string; document_id: string; note_text: string },
): Promise<void> {
  await okJson(
    await authFetch(token, '/staff/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  )
}

// ── Payments + same-child applications (Kate, 2026-10-29) ───────────────

export interface PaidItem { item_type: string; item_name: string; amount: number }
export interface ChildPayment { document_id: string; invoice_id: string; paid_at: string; amount: number; items: PaidItem[] }
export interface OtherApplication {
  document_id: string
  child_first_name: string | null
  child_last_name: string | null
  child_dob: string | null
  program: string | null
  location_name: string | null
  start_date: string | null
  document_status: string | null
  created_at: string | null
  merged_into: string | null
  hidden: boolean
  invoice_paid_at: string | null
}
export interface ChildPayments {
  payments: ChildPayment[]
  same_child: OtherApplication[]
  candidates: OtherApplication[]
  deposits_paid: number
}

export async function fetchChildPayments(token: string, documentId: string): Promise<ChildPayments> {
  return okJson(await authFetch(token, `/staff/enrollment/payments?document_id=${encodeURIComponent(documentId)}`))
}

export async function mergeApplication(token: string, documentId: string, primaryDocumentId: string): Promise<void> {
  await okJson(
    await authFetch(token, '/staff/enrollment/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_id: documentId, primary_document_id: primaryDocumentId }),
    }),
  )
}

export async function unmergeApplication(token: string, documentId: string): Promise<void> {
  await okJson(
    await authFetch(token, '/staff/enrollment/unmerge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_id: documentId }),
    }),
  )
}
